import { useCallback, useMemo } from "react";

import { useLensIdSearchParameter } from "../../lens-insights/hooks/useLensIdSearchParameter";
import { getLensActivityTotals } from "../api/queries/getLensActivityTotals";
import {
    type LensActivityTotalsQuery,
    type LensActivityTotalsQueryVariables,
} from "../api/queries/getLensActivityTotals.gql-generated";

import { useCursorPagedQuery } from "./useCursorPagedQuery";
import { useSelectedLens } from "./useSelectedLens";

type Response = LensActivityTotalsQuery["lensActivityTotals"];
interface LensPlusActivityTotals {
    rows: Response["lenses"][number][];
    totals: Response["creatorTotals"] | undefined;
    paidAmountMilliUsd: number | undefined;
    lensId: string | null;
    lensName: string | undefined;
    clearLensId: () => void;
    hasMore: boolean;
    fetchMore: () => void;
    loadingMore: boolean;
    hasMoreError: boolean;
    refetch: () => void;
    loading: boolean;
    hasError: boolean;
    hasRefreshError: boolean;
}

const PAGE = {
    cursor: (data: LensActivityTotalsQuery) => data.lensActivityTotals.cursor,
    merge: (previous: LensActivityTotalsQuery, next: LensActivityTotalsQuery): LensActivityTotalsQuery => {
        const existing = new Set(previous.lensActivityTotals.lenses.map((row) => row.unlockableId));
        return {
            ...previous,
            lensActivityTotals: {
                ...previous.lensActivityTotals,
                cursor: next.lensActivityTotals.cursor,
                lenses: [
                    ...previous.lensActivityTotals.lenses,
                    ...next.lensActivityTotals.lenses.filter((row) => !existing.has(row.unlockableId)),
                ],
            },
        };
    },
};

export const useLensPlusActivityTotals = (skip = false, ignoreSelection = false): LensPlusActivityTotals => {
    const { lensId: selectedLensId, clearLensId } = useLensIdSearchParameter();
    const lensId = ignoreSelection ? null : selectedLensId;
    const { lens, unlockableId, query: selectedLens } = useSelectedLens(lensId, skip);
    const variables = useMemo<LensActivityTotalsQueryVariables>(
        () => ({ input: { unlockableIds: unlockableId ? [unlockableId] : [], limit: 200 } }),
        [unlockableId],
    );
    const lensPending = Boolean(lensId && !unlockableId);
    const paged = useCursorPagedQuery<LensActivityTotalsQuery, LensActivityTotalsQueryVariables>(
        getLensActivityTotals,
        variables,
        skip || lensPending,
        PAGE,
    );
    const response = paged.response?.lensActivityTotals;
    const rows = useMemo(() => [...(response?.lenses ?? [])], [response?.lenses]);
    // A failed or unresolved Lens lookup is what blocks the page query, so retry that first.
    const retryLens = lensId && (Boolean(selectedLens.error) || !unlockableId) ? selectedLens.refetch : undefined;
    const refetch = useCallback(() => {
        if (!skip) paged.refetch(retryLens);
    }, [paged, retryLens, skip]);
    return {
        rows,
        totals: lensId ? rows.find((row) => row.unlockableId === unlockableId)?.totals : response?.creatorTotals,
        paidAmountMilliUsd: response?.paidAmountMilliUsd,
        lensId,
        lensName: lens?.name,
        clearLensId,
        hasMore: paged.hasMore,
        fetchMore: paged.fetchMore,
        loadingMore: paged.loadingMore,
        hasMoreError: paged.hasMoreError,
        refetch,
        loading: !skip && (Boolean(lensId && selectedLens.loading) || paged.loading),
        hasError:
            !skip &&
            (paged.hasError || Boolean(lensId && (selectedLens.error ?? (!selectedLens.loading && !unlockableId)))),
        hasRefreshError: paged.hasRefreshError,
    };
};
